import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { HUD_CARD, HUD_CTA_BUTTON } from './hudDialogStyles';

/**
 * Card genérico de aprovação de etapa (dados da etapa vêm da API — sem persistir no frontend).
 */
export default function EtapaAprovacaoGenerica({
  titulo = 'Aprovação',
  subtitulo = '',
  dadosSolicitante = null,
  children = null,
  observacaoLabel = 'Observação',
  observacaoObrigatoriaReprovar = true,
  loading = false,
  disabled = false,
  onAprovar,
  onReprovar,
  labelAprovar = 'Aprovar',
  labelReprovar = 'Reprovar',
}) {
  const [observacao, setObservacao] = useState('');
  const [erro, setErro] = useState('');

  const handleAprovar = async () => {
    setErro('');
    try {
      await onAprovar?.(observacao.trim());
      setObservacao('');
    } catch (e) {
      setErro(e?.message || 'Falha ao aprovar');
    }
  };

  const handleReprovar = async () => {
    setErro('');
    if (observacaoObrigatoriaReprovar && !observacao.trim()) {
      setErro('Informe a observação para reprovar.');
      return;
    }
    try {
      await onReprovar?.(observacao.trim());
      setObservacao('');
    } catch (e) {
      setErro(e?.message || 'Falha ao reprovar');
    }
  };

  return (
    <Box sx={{ ...HUD_CARD, p: 2.5 }}>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
        {titulo}
      </Typography>
      {subtitulo ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitulo}
        </Typography>
      ) : null}

      {dadosSolicitante && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'rgba(15,23,42,0.03)',
            border: '1px solid rgba(15,23,42,0.08)',
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
            Dados do solicitante
          </Typography>
          {dadosSolicitante.codigo && (
            <Typography variant="body2">
              <strong>Solicitação:</strong> {dadosSolicitante.codigo}
            </Typography>
          )}
          {dadosSolicitante.titulo && (
            <Typography variant="body2">
              <strong>Título:</strong> {dadosSolicitante.titulo}
            </Typography>
          )}
          {dadosSolicitante.unidade && (
            <Typography variant="body2">
              <strong>Unidade:</strong> {dadosSolicitante.unidade}
            </Typography>
          )}
        </Box>
      )}

      {children ? <Box sx={{ mb: 2 }}>{children}</Box> : null}

      <Divider sx={{ my: 2 }} />

      <TextField
        label={observacaoLabel}
        fullWidth
        multiline
        minRows={3}
        size="small"
        value={observacao}
        onChange={(e) => setObservacao(e.target.value)}
        disabled={disabled || loading}
      />

      {erro ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {erro}
        </Alert>
      ) : null}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          color="success"
          disabled={disabled || loading || !onAprovar}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
          onClick={handleAprovar}
          sx={{ ...HUD_CTA_BUTTON, textTransform: 'none', flex: 1 }}
        >
          {labelAprovar}
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={disabled || loading || !onReprovar}
          startIcon={<CancelOutlinedIcon />}
          onClick={handleReprovar}
          sx={{ textTransform: 'none', flex: 1, fontWeight: 700 }}
        >
          {labelReprovar}
        </Button>
      </Stack>
    </Box>
  );
}
